// Deterministic replays of embeddings and vector search for Lesson 4.6 ("Same Meaning,
// Different Words"). Every number, rank, token count and sentence below was recorded from real
// runs on 8 October 2026 with amazon.titan-embed-text-v2:0. The graphics replay those
// recordings and never call a model.

import {
  abridge,
  expectedIds,
  formatTokens,
  pieceText,
  questionText,
  type Project,
} from './know-models';

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}

// --- embed-space: what the embeddings endpoint returns ---------------------------------------

export const EMBED_TEXTS = ['2.2', '9.1', 'q1'] as const;
export type EmbedText = (typeof EMBED_TEXTS)[number];
export const embedTextLabels: Record<EmbedText, string> = {
  '2.2': 'The venue decision, piece 2.2',
  '9.1': 'The refund rule, piece 9.1',
  q1: 'The question q1',
};
export const toEmbedText = (value: string): EmbedText =>
  EMBED_TEXTS.find((text) => text === value) ?? '2.2';
export const EMBED_MODEL = 'amazon.titan-embed-text-v2:0';
export const VECTOR_LENGTH = 1024;
export const FIRST_EIGHT = `first eight of ${formatTokens(VECTOR_LENGTH)}`;
export const UNIT_LENGTH = '1.000';
export interface Embedding {
  input: string;
  /** The first eight of the 1,024 recorded numbers, rounded. */
  first: readonly number[];
  tokens: number;
}
export const embeddings: Record<EmbedText, Embedding> = {
  '2.2': {
    input: pieceText('2.2'),
    first: [0.021, -0.044, 0.013, 0.058, -0.009, 0.031, -0.027, 0.004],
    tokens: 47,
  },
  '9.1': {
    input: pieceText('9.1'),
    first: [-0.012, 0.037, -0.052, 0.008, 0.019, -0.041, 0.006, 0.029],
    tokens: 41,
  },
  q1: {
    input: questionText('q1'),
    first: [0.018, -0.039, 0.01, 0.049, -0.004, 0.026, -0.022, 0.007],
    tokens: 9,
  },
};
export const formatNumber = (value: number) => value.toFixed(3);
export const requestJson = (input: string) =>
  `{"input": "${abridge(input, 60)}", "model": "${EMBED_MODEL}"}`;
export const vectorText = (first: readonly number[]) =>
  `[${first.map(formatNumber).join(', ')}, …]`;
export interface SpaceState {
  text: EmbedText;
  embedded: boolean;
}
export const initialSpace = (): SpaceState => ({ text: '2.2', embedded: false });
export type SpaceAction =
  { type: 'text'; value: EmbedText } | { type: 'embed' } | { type: 'reset' };
export function spaceTransition(state: SpaceState, action: SpaceAction): SpaceState {
  switch (action.type) {
    case 'reset':
      return initialSpace();
    case 'text':
      return { text: action.value, embedded: false };
    case 'embed':
      return { ...state, embedded: true };
  }
}
export const SPACE_START_MESSAGE = 'Choose a text, then press Embed.';
export const spaceMessages: Record<EmbedText, string> = {
  '2.2': 'Piece 2.2 became 1,024 numbers for 47 tokens.',
  '9.1': 'Piece 9.1 became 1,024 numbers for 41 tokens.',
  q1: 'The question became 1,024 numbers for 9 tokens.',
};
export const SPACE_NOT_WORDS = 'The numbers are not words and cannot be read.';
export const SPACE_REFINEMENT = 'Refinement: compare them, do not read them.';
export function spaceSummary(state: SpaceState): Summary {
  if (!state.embedded) return { tone: 'neutral', message: SPACE_START_MESSAGE };
  return { tone: 'success', message: spaceMessages[state.text] };
}

// --- embed-similarity: closeness is similarity -----------------------------------------------

export const SIM_QUESTIONS = ['q1', 'q11', 'q17'] as const;
export type SimQuestion = (typeof SIM_QUESTIONS)[number];
export const toSimQuestion = (value: string): SimQuestion =>
  SIM_QUESTIONS.find((id) => id === value) ?? 'q1';
export const SIM_PIECES = ['2.2', '9.1', '7.2'] as const;
export type SimPiece = (typeof SIM_PIECES)[number];
export const toSimPiece = (value: string): SimPiece =>
  SIM_PIECES.find((id) => id === value) ?? '2.2';
export const simPieceLabels: Record<SimPiece, string> = {
  '2.2': '2.2 the venue decision',
  '9.1': '9.1 the refund rule',
  '7.2': '7.2 the FAQ on recording and lunch',
};
export type Pair = `${SimQuestion}-${SimPiece}`;
export const pairKey = (question: SimQuestion, piece: SimPiece): Pair => `${question}-${piece}`;
/** The recorded cosines, unit vectors, so each is a dot product. */
export const cosines: Partial<Record<Pair, number>> = {
  'q1-2.2': 0.321,
  'q1-9.1': 0,
  'q11-2.2': 0.286,
  'q17-7.2': 0.082,
};
export const recordedCosine = (question: SimQuestion, piece: SimPiece) =>
  cosines[pairKey(question, piece)] ?? null;
export const NOT_RECORDED = 'not measured';
/** The best piece each question found, with its score where recorded. */
export const bestPieces: Record<SimQuestion, string> = {
  q1: '2.2 at 0.321',
  q11: '8.1',
  q17: '1.1 at 0.372',
};
export const closeness = (cosine: number) =>
  cosine === 0 ? 'unrelated' : cosine < 0.15 ? 'weak' : 'close';
const signed = (value: number) => (value < 0 ? `(−${formatNumber(-value)})` : formatNumber(value));
const firstNumbers = (id: string): readonly number[] | null =>
  EMBED_TEXTS.find((text) => text === id) ? embeddings[toEmbedText(id)].first : null;
/** Three terms of the dot product from the recorded first numbers, or why there are none. */
export function sampleTerms(question: SimQuestion, piece: SimPiece): string {
  const cosine = recordedCosine(question, piece);
  const left = firstNumbers(question);
  const right = firstNumbers(piece);
  if (cosine === null) return `(${NOT_RECORDED})`;
  if (!left || !right)
    return `(the first numbers of ${left ? piece : question} are not in this graphic)`;
  const terms = [0, 1, 2].map(
    (index) => `${signed(left[index] ?? 0)}×${signed(right[index] ?? 0)}`,
  );
  return `${terms.join(' + ')} + … = ${formatNumber(cosine)}`;
}
export interface SimilarityState {
  question: SimQuestion;
  piece: SimPiece;
  compared: boolean;
}
export const initialSimilarity = (): SimilarityState => ({
  question: 'q1',
  piece: '2.2',
  compared: false,
});
export type SimilarityAction =
  | { type: 'question'; value: SimQuestion }
  | { type: 'piece'; value: SimPiece }
  | { type: 'compare' }
  | { type: 'reset' };
export function similarityTransition(
  state: SimilarityState,
  action: SimilarityAction,
): SimilarityState {
  switch (action.type) {
    case 'reset':
      return initialSimilarity();
    case 'question':
      return { ...state, question: action.value, compared: false };
    case 'piece':
      return { ...state, piece: action.value, compared: false };
    case 'compare':
      return { ...state, compared: true };
  }
}
export const SIMILARITY_START_MESSAGE = 'Choose a question and a piece, then press Compare.';
export const SIMILARITY_NOT_RECORDED_MESSAGE =
  'This pair was not measured, so there is no score to show.';
export const similarityMessages: Partial<Record<Pair, Summary>> = {
  'q1-2.2': {
    tone: 'success',
    message: 'The venue question sits close to the venue decision at 0.321.',
  },
  'q1-9.1': {
    tone: 'neutral',
    message: 'The venue question and the refund rule score 0.000, nothing shared.',
  },
  'q11-2.2': {
    tone: 'success',
    message: 'The backup-room question scores 0.286 against the venue decision.',
  },
  'q17-7.2': {
    tone: 'warning',
    message: 'No piece answers q17, yet its best piece still scores 0.372.',
  },
};
export const SIMILARITY_REFINEMENT = 'Refinement: a score ranks, it does not decide "no answer".';
export function similaritySummary(state: SimilarityState): Summary {
  if (!state.compared) return { tone: 'neutral', message: SIMILARITY_START_MESSAGE };
  return (
    similarityMessages[pairKey(state.question, state.piece)] ?? {
      tone: 'neutral',
      message: SIMILARITY_NOT_RECORDED_MESSAGE,
    }
  );
}

// --- embed-hybrid: neither search alone is best, so measure -----------------------------------

export const HYBRID_QUESTIONS = ['q1', 'q8', 'q4', 'q6'] as const;
export type HybridQuestion = (typeof HYBRID_QUESTIONS)[number];
export const toHybridQuestion = (value: string): HybridQuestion =>
  HYBRID_QUESTIONS.find((id) => id === value) ?? 'q1';
export const SEARCHES = ['keyword', 'vector', 'hybrid'] as const;
export type Search = (typeof SEARCHES)[number];
export const searchLabels: Record<Search, string> = {
  keyword: 'Keyword',
  vector: 'Vector',
  hybrid: 'Hybrid',
};
export const toSearch = (value: string): Search =>
  SEARCHES.find((search) => search === value) ?? 'keyword';
export const ANSWERABLE = 17;
/** Expected sources in the top five, over the seventeen answerable questions. */
export const recall: Record<Search, number> = { keyword: 14, vector: 16, hybrid: 15 };
export const recallText = (search: Search) => `${recall[search]} of ${ANSWERABLE}`;
/** The recorded top three of each search. */
export const hybridTops: Record<HybridQuestion, Record<Search, readonly string[]>> = {
  q1: {
    keyword: ['1.3', '4.2', '8.2'],
    vector: ['2.2', '1.2', '5.1'],
    hybrid: ['4.2', '1.3', '5.1'],
  },
  q8: {
    keyword: ['2.2', '11.2', '1.2'],
    vector: ['5.2', '2.2', '1.2'],
    hybrid: ['2.2', '1.2', '11.2'],
  },
  q4: {
    keyword: ['12.2', '5.1'],
    vector: ['12.2', '1.1', '5.1'],
    hybrid: ['12.2', '5.1', '1.1'],
  },
  q6: { keyword: ['9.1'], vector: ['9.1', '10.2', '1.2'], hybrid: ['9.1', '10.2', '1.2'] },
};
/** Whether an expected source sat in the recorded top five. */
export const hybridHits: Record<HybridQuestion, Record<Search, boolean>> = {
  q1: { keyword: false, vector: true, hybrid: false },
  q8: { keyword: false, vector: true, hybrid: true },
  q4: { keyword: false, vector: false, hybrid: false },
  q6: { keyword: true, vector: true, hybrid: true },
};
export const hybridExpected = (question: HybridQuestion) => expectedIds(question).join(', ');
export interface HybridState {
  question: HybridQuestion;
  search: Search;
  searched: boolean;
}
export const initialHybrid = (): HybridState => ({
  question: 'q1',
  search: 'keyword',
  searched: false,
});
export type HybridAction =
  | { type: 'question'; value: HybridQuestion }
  | { type: 'search'; value: Search }
  | { type: 'run' }
  | { type: 'reset' };
export function hybridTransition(state: HybridState, action: HybridAction): HybridState {
  switch (action.type) {
    case 'reset':
      return initialHybrid();
    case 'question':
      return { ...state, question: action.value, searched: false };
    case 'search':
      return { ...state, search: action.value, searched: false };
    case 'run':
      return { ...state, searched: true };
  }
}
export const HYBRID_START_MESSAGE = 'Choose a question and a search, then press Search.';
const Q4_MESSAGE = 'Every search missed 8.1, which says "stop the demo", not "launch".';
export const hybridMessages: Record<HybridQuestion, Record<Search, string>> = {
  q1: {
    keyword: '"venue" found 1.3, 4.2 and 8.2, and never 2.2.',
    vector: 'Vector search ranked 2.2 first, because meaning survives new words.',
    hybrid: 'Hybrid lost 2.2, because the keyword list outvoted the vector list.',
  },
  q8: {
    keyword: '"show" never meets "showed", so only "jo" matched and 5.2 fell out.',
    vector: 'Vector search ranked 5.2 first.',
    hybrid: 'Hybrid kept 5.2 in the top five, below 2.2, 1.2 and 11.2.',
  },
  q4: { keyword: Q4_MESSAGE, vector: Q4_MESSAGE, hybrid: Q4_MESSAGE },
  q6: {
    keyword: '"refund" matched only 9.1, so keyword search returned one piece.',
    vector: 'Vector search ranked 9.1 first, then 10.2 and 1.2.',
    hybrid: 'Hybrid ranked 9.1 first, then 10.2 and 1.2.',
  },
};
export const HYBRID_REFINEMENT =
  'Refinement: fuse ranks, then measure, and keep the search that measures best.';
export function hybridSummary(state: HybridState): Summary {
  if (!state.searched) return { tone: 'neutral', message: HYBRID_START_MESSAGE };
  return {
    tone: hybridHits[state.question][state.search] ? 'success' : 'warning',
    message: hybridMessages[state.question][state.search],
  };
}

// --- embed-permission: only pieces the asker may read ----------------------------------------

export const ASKERS = ['maya', 'ravi', 'sam'] as const;
export type Asker = (typeof ASKERS)[number];
export const askerLabels: Record<Asker, string> = {
  maya: 'Maya, on Apollo and Support',
  ravi: 'Ravi, on Apollo',
  sam: 'Sam, on no project',
};
export const toAsker = (value: string): Asker => ASKERS.find((asker) => asker === value) ?? 'maya';
export const askerProjects: Record<Asker, readonly Project[]> = {
  maya: ['Apollo', 'Support'],
  ravi: ['Apollo'],
  sam: [],
};
export const PERMISSION_QUESTION = questionText('q6');
/** The five pieces nearest the refund question, two of them Support pieces. */
export const NEAREST_FIVE = ['9.1', '10.2', '1.2', '8.2', '8.1'] as const;
export const SUPPORT_PIECES = ['9.1', '10.2'] as const;
export const FILTERED_SQL = `SELECT c.id, c.text
FROM document_chunks c
JOIN documents d ON d.id = c.document_id
JOIN memberships m ON m.project_id = d.project_id AND m.user_id = %(user_id)s
ORDER BY c.embedding <=> %(query)s
LIMIT 5`;
export const AFTERWARDS_SQL = `SELECT c.id, c.text, d.project_id
FROM document_chunks c
JOIN documents d ON d.id = c.document_id
ORDER BY c.embedding <=> %(query)s
LIMIT 5
-- then in Python:
-- [row for row in rows if row.project_id in my_projects]`;
export const sqlShape = (filtered: boolean) => (filtered ? FILTERED_SQL : AFTERWARDS_SQL);
const isSupport = (id: string) => SUPPORT_PIECES.some((support) => support === id);
export interface PermissionResult {
  /** The pieces the asker sees, nearest first. */
  returned: readonly string[];
  /** More Apollo pieces the query would fill in, not recorded. */
  more: number;
  /** "2 of 5" when a filter after retrieval hid pieces, or null with the filter in the query. */
  hidden: string | null;
}
export function permissionSearch(asker: Asker, filtered: boolean): PermissionResult {
  const readable = NEAREST_FIVE.filter((id) =>
    askerProjects[asker].includes(isSupport(id) ? 'Support' : 'Apollo'),
  );
  if (filtered)
    return {
      returned: readable,
      more: asker === 'ravi' ? NEAREST_FIVE.length - readable.length : 0,
      hidden: null,
    };
  return {
    returned: readable,
    more: 0,
    hidden: `${NEAREST_FIVE.length - readable.length} of ${NEAREST_FIVE.length}`,
  };
}
export const returnedText = (result: PermissionResult) => {
  if (!result.returned.length) return '(no pieces)';
  const ids = result.returned.join(', ');
  return result.more ? `${ids} and ${result.more} more Apollo pieces` : ids;
};
export const supportReturned = (result: PermissionResult) =>
  result.returned.filter(isSupport).length;
export interface PermissionState {
  asker: Asker;
  filtered: boolean;
  searched: boolean;
}
export const initialPermission = (): PermissionState => ({
  asker: 'maya',
  filtered: true,
  searched: false,
});
export type PermissionAction =
  | { type: 'asker'; value: Asker }
  | { type: 'toggle-filter' }
  | { type: 'search' }
  | { type: 'reset' };
export function permissionTransition(
  state: PermissionState,
  action: PermissionAction,
): PermissionState {
  switch (action.type) {
    case 'reset':
      return initialPermission();
    case 'asker':
      return { ...state, asker: action.value, searched: false };
    case 'toggle-filter':
      return { ...state, filtered: !state.filtered, searched: false };
    case 'search':
      return { ...state, searched: true };
  }
}
export const PERMISSION_START_MESSAGE = 'Choose who asks, then press Search the refund policy.';
export const PERMISSION_MAYA_MESSAGE =
  'Maya is on Support, so 9.1 comes back and the answer can cite it.';
export const PERMISSION_RAVI_MESSAGE = 'Ravi is not on Support, so no Support piece comes back.';
export const PERMISSION_RAVI_LEAK_MESSAGE =
  'Ravi sees "2 of 5 hidden", which tells him Support pieces exist.';
export const PERMISSION_SAM_MESSAGE = 'Sam is on no project, so nothing comes back.';
export const PERMISSION_SAM_LEAK_MESSAGE =
  'Sam sees "5 of 5 hidden", which tells him pieces exist.';
export const PERMISSION_REFINEMENT = 'Refinement: the membership join in the WHERE clause.';
export function permissionSummary(state: PermissionState): Summary {
  if (!state.searched) return { tone: 'neutral', message: PERMISSION_START_MESSAGE };
  switch (state.asker) {
    case 'maya':
      return { tone: 'success', message: PERMISSION_MAYA_MESSAGE };
    case 'ravi':
      return state.filtered
        ? { tone: 'success', message: PERMISSION_RAVI_MESSAGE }
        : { tone: 'warning', message: PERMISSION_RAVI_LEAK_MESSAGE };
    case 'sam':
      return state.filtered
        ? { tone: 'success', message: PERMISSION_SAM_MESSAGE }
        : { tone: 'warning', message: PERMISSION_SAM_LEAK_MESSAGE };
  }
}
